// import { Loader } from "@/services/helper";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";
import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import ErrorPage from "@/views/errors/404.vue";
import AccessDeniedPage from "@/views/errors/access-denied.vue";
import { $eventHub } from "@/components";
import { EventConstraints } from "@/enums/events.enum";

import AuthRoutes from "@/views/auth/routes.ts";
import SettingRoutes from "@/views/settings/routes.ts";
import CustomerRoutes from "@/views/customers/routes.ts";
import CallCenterRoutes from "@/views/call-center/routes";
import OrdersRoutes from "@/views/orders/routes";
import CatalogRoutes from "@/views/catalog/routes.ts";
import OOCRoutes from "@/views/ooc/routes.ts";
import { AppsConstraints } from "@/enums/apps.enum";
import { LinksConstrains } from "@/enums/links.enum";
import { Helper } from "@/services/helper";
import store from "@/store";
import { Translate as $t } from "@/plugins/translate";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "menu.dashboard",
    component: () => import("../views/Dashboard.vue"),
    meta: { isAuth: true, service: "dashboard", title: "POSRocket | Dashboard" },
  },
  {
    path: "/reports",
    name: "menu.reports",
    component: () => import("../views/reports/Reports.vue"),
    meta: { isAuth: true, service: "Reports", title: "POSRocket | Reports" },
  },

  {
    path: "/editable-table",
    name: "editable-table",
    component: () => import("../views/reports/editable-table.vue"),
    meta: { isAuth: true, service: "Reports", title: "POSRocket | Reports" },
  },
  {
    path: "/app/translation/en",
    name: "translation",
    component: () => import("../views/translation/en.vue"),
    meta: { title: "POSRocket | EN" },
  },
  {
    path: "/app/translation/ar",
    name: "translation-ar",
    component: () => import("../views/translation/ar.vue"),
    meta: { title: "POSRocket | Ar" },
  },
  ...AuthRoutes,
  ...CustomerRoutes,
  ...OrdersRoutes,
  ...SettingRoutes,
  ...CallCenterRoutes,
  ...CatalogRoutes,

  ...OOCRoutes,

  {
    path: "/access-denied",
    name: "access-denied",
    component: AccessDeniedPage,
    meta: { title: "POSRocket | Access Denied", app: AppsConstraints.ERROR },
  },

  {
    path: "*",
    name: "404",
    component: ErrorPage,
    meta: { title: "POSRocket | 404 not found", app: AppsConstraints.ERROR },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

const oocAppChecker = (to, from, next) => {
  if (process.env.VUE_APP_SHOW_OOC_APP === "false") {
    return next("/404");
  }

  const user = Storage.get(StorageConstants.OOC_SESSION) || null;

  const { isAuth, title } = to?.meta;

  document.title = title || "";

  // user logged in and route login
  if (to.fullPath.match(/^\/login(?:\/(?=$))?$/i) && user) return next({ path: "/ooc/" });

  // route required auth and user not logged in
  if (isAuth && !user) return next({ path: LinksConstrains.OOC_LOGIN, params: { nextUrl: to.fullPath } });

  // otherwise
  return next();
};

router.beforeEach((to, from, next) => {
  // Stop Using UAT Route
  if (window?.location?.origin?.includes("uat-app.posrocket.com")) {
    return (window.location.href = process.env.VUE_APP_PROD_DOMAIN_URL);
  }

  if (to?.meta?.app === AppsConstraints.OOC) {
    return oocAppChecker(to, from, next);
  }

  setTimeout(() => {
    // @ts-ignore
    from = null;

    // @ts-ignore
    const $events = $eventHub._events;
    for (const propName in $events) {
      if (window._.isEmpty($events[propName])) {
        delete $events[propName];
      }
    }
    // @ts-ignore
    $eventHub._events = $events;
  }, 100);

  const { isAuth, title } = to?.meta;

  document.title = title || "";

  const user = Storage.get(StorageConstants.SESSION);

  //TODO: Delete this code
  // force redirect users to call center when the VUE_APP_FORCE_CALL_CENTER is true
  if (
    to?.meta?.app !== AppsConstraints.ERROR &&
    to?.meta?.app !== AppsConstraints.CALL_CENTER &&
    process.env.VUE_APP_FORCE_CALL_CENTER === "true" &&
    !!user
  ) {
    return next({ path: LinksConstrains.CALL_CENTER_DASHBOARD });
  }

  // user logged in and route login
  if (to.fullPath.match(/^\/login(?:\/(?=$))?$/i) && user) {
    return next({ path: "/", params: { nextUrl: to.fullPath } });
  }

  // route required auth and user not logged in
  if (isAuth && !user) {
    Storage.set(StorageConstants.NEXT_ROUTE, to.fullPath);
    return next({ path: "/login", params: { nextUrl: to.fullPath } });
  }

  //TODO: Delete this code
  // check if the user allowed to use call center
  if (to?.meta?.app === AppsConstraints.CALL_CENTER && !Helper.isUserAllowedCallCenter()) {
    store.commit("User/SET_IS_USER_AUTHORIZED", false);
    store.commit("User/SET_IS_USER_MESSAGE", { success: false, message: $t("access_denied_title") });
    return next(LinksConstrains.ACCESS_DENIED);
  }

  // otherwise
  return next();
});

// router.beforeResolve((to, from, next) => {
//   next();
// });

router.afterEach(() => {
  // hide app loader
  $eventHub.$emit(EventConstraints.HIDE_APP_LOADER, 500);
});

export default router;
